"use client"

import { useParams, useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { 
  ArrowLeft, 
  Package, 
  Clock, 
  CheckCircle2, 
  ChefHat, 
  Truck, 
  MapPin, 
  XCircle,
  Phone,
  Mail,
  CreditCard,
  Receipt,
  Calendar,
  Copy,
  Check,
  Wallet,
  Loader2,
  AlertCircle
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { SEO } from "@/components/seo"
import { useGetOrderByAccessTokenQuery, useInitializePaymentMutation, useGetPublicSettingsQuery } from "@/store"
import type { OrderStatus } from "@/types/order"
import { useState } from "react"
import { toast } from "sonner"

type PaymentProvider = "paystack" | "flutterwave"

// Order status configuration with icons and colors
const ORDER_STATUSES: {
  key: OrderStatus
  label: string
  description: string
  icon: React.ElementType
  color: string
  bgColor: string
}[] = [
  {
    key: "PENDING",
    label: "Order Placed",
    description: "Your order has been received",
    icon: Receipt,
    color: "text-blue-600",
    bgColor: "bg-blue-100 dark:bg-blue-900/30",
  },
  {
    key: "CONFIRMED",
    label: "Confirmed",
    description: "Order confirmed by restaurant",
    icon: CheckCircle2,
    color: "text-green-600",
    bgColor: "bg-green-100 dark:bg-green-900/30",
  },
  {
    key: "PREPARING",
    label: "Preparing",
    description: "Your meal is being prepared",
    icon: ChefHat,
    color: "text-orange-600",
    bgColor: "bg-orange-100 dark:bg-orange-900/30",
  },
  {
    key: "READY",
    label: "Ready",
    description: "Order is ready for pickup/delivery",
    icon: Package,
    color: "text-purple-600",
    bgColor: "bg-purple-100 dark:bg-purple-900/30",
  },
  {
    key: "OUT_FOR_DELIVERY",
    label: "Out for Delivery",
    description: "On the way to your location",
    icon: Truck,
    color: "text-indigo-600",
    bgColor: "bg-indigo-100 dark:bg-indigo-900/30",
  },
  {
    key: "DELIVERED",
    label: "Delivered",
    description: "Order delivered successfully",
    icon: CheckCircle2,
    color: "text-emerald-600",
    bgColor: "bg-emerald-100 dark:bg-emerald-900/30",
  },
]

const CANCELLED_STATUS = {
  key: "CANCELLED" as OrderStatus,
  label: "Cancelled",
  description: "Order has been cancelled",
  icon: XCircle,
  color: "text-red-600",
  bgColor: "bg-red-100 dark:bg-red-900/30",
}

function getStatusIndex(status: OrderStatus): number {
  if (status === "CANCELLED") return -1
  return ORDER_STATUSES.findIndex((s) => s.key === status)
}

function getPaymentStatusBadge(status: string) {
  switch (status) {
    case "PAID":
      return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Paid</Badge>
    case "PENDING":
      return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">Pending</Badge>
    case "FAILED":
      return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">Failed</Badge>
    case "REFUNDED":
      return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">Refunded</Badge>
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-NG", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export default function OrderTrackingScreen() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [copied, setCopied] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<PaymentProvider>("paystack")
  const [initializePayment, { isLoading: isInitializingPayment }] = useInitializePaymentMutation()
  const { data: publicSettings } = useGetPublicSettingsQuery();
  const businessInfo = publicSettings?.data || {};
  
  const defaultPhone = "09017104173";
  const defaultEmail = "zestandzeallunches@gmail.com";

  const { data: orderData, isLoading, error } = useGetOrderByAccessTokenQuery(token || "", {
    skip: !token,
    // Poll for updates every 30 seconds
    pollingInterval: 30000,
  })

  const order = orderData?.data

  const handleCopyOrderId = () => {
    if (order) {
      navigator.clipboard.writeText(order.displayOrderId)
      setCopied(true)
      toast.success("Order ID copied to clipboard")
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handlePayNow = async () => {
    if (!order) return

    try {
      const result = await initializePayment({
        orderId: order.id,
        provider: selectedProvider,
      }).unwrap()

      if (result.success && result.data.redirectUrl) {
        // Redirect to payment provider
        window.location.href = result.data.redirectUrl
      } else {
        toast.error("Failed to initialize payment. Please try again.")
      }
    } catch (err: any) {
      console.error("Payment initialization error:", err)
      toast.error(err.data?.message || "Failed to initialize payment. Please try again.")
    }
  }

  // Check if payment is pending and order is not cancelled
  const canPayNow = order && 
    order.paymentStatus === "PENDING" && 
    order.status !== "CANCELLED" &&
    order.paymentMethod === "ONLINE"

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-8">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="h-64 bg-muted rounded-lg" />
              <div className="h-48 bg-muted rounded-lg" />
            </div>
            <div className="h-96 bg-muted rounded-lg" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto"
        >
          <div className="mb-6 inline-flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 p-6">
            <XCircle className="h-12 w-12 text-red-600" />
          </div>
          <h2 className="text-3xl font-bold mb-4">Order Not Found</h2>
          <p className="text-muted-foreground mb-6">
            The order you're looking for doesn't exist or the link has expired.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate("/track-order")}>
              Track Another Order
            </Button>
            <Button variant="outline" onClick={() => navigate("/")}>
              Go to Homepage
            </Button>
          </div>
        </motion.div>
      </div>
    )
  }

  const currentStatusIndex = getStatusIndex(order.status)
  const isCancelled = order.status === "CANCELLED"

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Back Button */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </motion.div>

      <SEO
        title={`Order #${order.displayOrderId}`}
        description="Track your order status and view order details"
        noindex={true}
      />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl md:text-4xl font-bold">Order #{order.displayOrderId}</h1>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleCopyOrderId}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-muted-foreground flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Placed on {formatDate(order.createdAt)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {getPaymentStatusBadge(order.paymentStatus)}
            {isCancelled ? (
              <Badge variant="destructive">Cancelled</Badge>
            ) : (
              <Badge className="bg-gradient-to-r from-orange-500 to-amber-500 text-white">
                {ORDER_STATUSES[currentStatusIndex]?.label || order.status}
              </Badge>
            )}
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Status Timeline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Order Status
                </CardTitle>
                <CardDescription>
                  {isCancelled 
                    ? "This order has been cancelled" 
                    : "Track your order progress in real-time"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isCancelled ? (
                  <div className="flex items-center gap-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <div className={`p-3 rounded-full ${CANCELLED_STATUS.bgColor}`}>
                      <XCircle className={`h-6 w-6 ${CANCELLED_STATUS.color}`} />
                    </div>
                    <div>
                      <p className="font-semibold text-red-800 dark:text-red-400">
                        Order Cancelled
                      </p>
                      <p className="text-sm text-red-600 dark:text-red-500">
                        This order was cancelled. If you have any questions, please contact us.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Timeline */}
                    <div className="space-y-0">
                      {ORDER_STATUSES.map((status, index) => {
                        const isCompleted = index <= currentStatusIndex
                        const isCurrent = index === currentStatusIndex
                        const Icon = status.icon

                        return (
                          <div key={status.key} className="relative flex gap-4">
                            {/* Connector Line */}
                            {index < ORDER_STATUSES.length - 1 && (
                              <div
                                className={`absolute left-5 top-10 w-0.5 h-full -translate-x-1/2 transition-colors duration-500 ${
                                  index < currentStatusIndex
                                    ? "bg-gradient-to-b from-orange-500 to-amber-500"
                                    : "bg-muted"
                                }`}
                              />
                            )}

                            {/* Status Icon */}
                            <div
                              className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full transition-all duration-500 ${
                                isCompleted
                                  ? isCurrent
                                    ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white ring-4 ring-orange-500/20"
                                    : "bg-gradient-to-r from-orange-500 to-amber-500 text-white"
                                  : "bg-muted text-muted-foreground"
                              }`}
                            >
                              <Icon className="h-5 w-5" />
                            </div>

                            {/* Status Info */}
                            <div className={`flex-1 pb-8 ${index === ORDER_STATUSES.length - 1 ? "pb-0" : ""}`}>
                              <div
                                className={`p-4 rounded-lg transition-all duration-300 ${
                                  isCurrent
                                    ? "bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20"
                                    : isCompleted
                                    ? "bg-muted/50"
                                    : ""
                                }`}
                              >
                                <p
                                  className={`font-semibold ${
                                    isCompleted ? "text-foreground" : "text-muted-foreground"
                                  }`}
                                >
                                  {status.label}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {status.description}
                                </p>
                                {isCurrent && (
                                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 font-medium">
                                    Current Status
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Order Items */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Order Items
                </CardTitle>
                <CardDescription>
                  {order.items.length} item{order.items.length !== 1 ? "s" : ""} in your order
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {order.items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-4 p-3 rounded-lg bg-muted/50"
                    >
                      {item.product?.image && (
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-card shrink-0">
                          <img
                            src={item.product.image}
                            alt={item.product?.name || "Product"}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold truncate">
                          {item.product?.name || "Product"}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Qty: {item.quantity} × ₦{item.price.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">₦{item.subtotal.toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card>
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>₦{order.subtotal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <span className={order.tax === 0 ? "text-green-600" : ""}>
                      {order.tax === 0 ? "Free" : `₦${order.tax.toLocaleString()}`}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Delivery Fee</span>
                    <span className={order.deliveryFee === 0 ? "text-green-600" : ""}>
                      {order.deliveryFee === 0 ? "Free" : `₦${order.deliveryFee.toLocaleString()}`}
                    </span>
                  </div>
                  {order.discount && order.discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Discount</span>
                      <span className="text-green-600">-₦{order.discount.toLocaleString()}</span>
                    </div>
                  )}
                </div>
                <Separator />
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>₦{order.total.toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Pay Now Card - Only show if payment is pending */}
          {canPayNow && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
            >
              <Card className="border-2 border-orange-500/50 bg-gradient-to-br from-orange-500/5 to-amber-500/5">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-full bg-orange-500/10">
                      <AlertCircle className="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Payment Required</CardTitle>
                      <CardDescription>
                        Complete your payment to process your order
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                    <p className="text-sm text-yellow-800 dark:text-yellow-400">
                      Your order is awaiting payment. Please complete the payment to avoid order cancellation.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Select Payment Method</Label>
                    <RadioGroup 
                      value={selectedProvider} 
                      onValueChange={(value) => setSelectedProvider(value as PaymentProvider)}
                      className="space-y-2"
                    >
                      <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors">
                        <RadioGroupItem value="paystack" id="paystack" />
                        <Label htmlFor="paystack" className="flex-1 cursor-pointer flex items-center gap-2">
                          <CreditCard className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="font-medium text-sm">Paystack</div>
                            <div className="text-xs text-muted-foreground">Card, Bank Transfer, USSD</div>
                          </div>
                        </Label>
                      </div>
                      <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors">
                        <RadioGroupItem value="flutterwave" id="flutterwave" />
                        <Label htmlFor="flutterwave" className="flex-1 cursor-pointer flex items-center gap-2">
                          <Wallet className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="font-medium text-sm">Flutterwave</div>
                            <div className="text-xs text-muted-foreground">Card, Bank Transfer, Mobile Money</div>
                          </div>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <Separator />

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Amount to Pay</span>
                    <span className="text-xl font-bold">₦{order.total.toLocaleString()}</span>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button 
                    onClick={handlePayNow}
                    disabled={isInitializingPayment}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:opacity-90 text-white"
                    size="lg"
                  >
                    {isInitializingPayment ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Pay Now - ₦{order.total.toLocaleString()}
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          )}

          {/* Delivery Address */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: canPayNow ? 0.45 : 0.4 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Delivery Address
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1 text-sm">
                  <p className="font-medium">{order.deliveryAddress.label}</p>
                  <p className="text-muted-foreground">{order.deliveryAddress.street}</p>
                  <p className="text-muted-foreground">
                    {order.deliveryAddress.city}, {order.deliveryAddress.state}
                  </p>
                  {order.deliveryAddress.zipCode && (
                    <p className="text-muted-foreground">{order.deliveryAddress.zipCode}</p>
                  )}
                  <p className="text-muted-foreground">
                    {order.deliveryAddress.country || "Nigeria"}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Payment Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: canPayNow ? 0.55 : 0.5 }}
          >
            <Card className={canPayNow ? "border-yellow-500/50" : ""}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Payment Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Method</span>
                  <span className="font-medium">{order.paymentMethod}</span>
                </div>
                {order.paymentProvider && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Provider</span>
                    <span className="font-medium capitalize">{order.paymentProvider.toLowerCase()}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status</span>
                  {getPaymentStatusBadge(order.paymentStatus)}
                </div>
                {canPayNow && (
                  <>
                    <Separator />
                    <Button 
                      onClick={handlePayNow}
                      disabled={isInitializingPayment}
                      variant="outline"
                      className="w-full border-orange-500 text-orange-600 hover:bg-orange-500 hover:text-white"
                      size="sm"
                    >
                      {isInitializingPayment ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="mr-2 h-4 w-4" />
                          Complete Payment
                        </>
                      )}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Need Help */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: canPayNow ? 0.65 : 0.6 }}
          >
            <Card className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border-orange-500/20">
              <CardHeader>
                <CardTitle className="text-lg">Need Help?</CardTitle>
                <CardDescription>
                  Contact us if you have any questions about your order
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <a
                  href={`tel:${businessInfo.phone || defaultPhone}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-background hover:bg-muted transition-colors"
                >
                  <Phone className="h-5 w-5 text-orange-500" />
                  <span className="font-medium">{businessInfo.phone || defaultPhone}</span>
                </a>
                <a
                  href={`mailto:${businessInfo.email || defaultEmail}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-background hover:bg-muted transition-colors"
                >
                  <Mail className="h-5 w-5 text-orange-500" />
                  <span className="font-medium">{businessInfo.email || defaultEmail}</span>
                </a>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
